import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../../components/layouts/MainLayout';
import Card from '../../../components/common/Card';
import { useToast } from '../../../components/common/Toast';
import DocumentUploadCard from '../../../components/seller/DocumentUploadCard';
import FormInput from '../../../components/forms/FormInput';
import Button from '../../../components/common/Button';
import { sellerService } from '../../../services/seller.service';
import { DOCUMENT_INFO } from '../../../config/constants';
import { validators } from '../../../utils/validators';
import { theme } from '../../../config/theme';

const CompleteProfile = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('documents');
  const [uploads, setUploads] = useState({});
  const [bankData, setBankData] = useState({
    bank_name: '',
    bank_account_number: '',
    confirm_account_number: '',
    ifsc_code: '',
    branch_name: '',
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);

  const handleUpload = (docType, data) => {
    setUploads(prev => ({ ...prev, [docType]: data }));
  };

  const handleBankChange = (e) => {
    const { name, value } = e.target;
    setBankData(prev => ({ ...prev, [name]: value }));
    if (touched[name]) validateField(name, value);
  };

  const handleBankBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  const validateField = (name, value) => {
    let error = null;
    if (name === 'bank_name') error = validators.required(value, 'Bank name');
    else if (name === 'bank_account_number') error = validators.accountNumber(value);
    else if (name === 'confirm_account_number' && value !== bankData.bank_account_number) error = 'Account numbers do not match';
    else if (name === 'ifsc_code') error = validators.ifsc(value);
    
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const validateBank = () => {
    const newErrors = {};
    const newTouched = {};
    
    newTouched.bank_name = true;
    newTouched.bank_account_number = true;
    newTouched.confirm_account_number = true;
    newTouched.ifsc_code = true;
    
    newErrors.bank_name = validators.required(bankData.bank_name, 'Bank name');
    newErrors.bank_account_number = validators.accountNumber(bankData.bank_account_number);
    if (bankData.bank_account_number !== bankData.confirm_account_number) {
      newErrors.confirm_account_number = 'Account numbers do not match';
    }
    newErrors.ifsc_code = validators.ifsc(bankData.ifsc_code);

    const filtered = Object.entries(newErrors).filter(([_, v]) => v).reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});
    setErrors(filtered);
    setTouched(newTouched);
    return Object.keys(filtered).length === 0;
  };

  const handleIFSCBlur = async () => {
    if (bankData.ifsc_code.length === 11) {
      try {
        const response = await fetch(`https://ifsc.razorpay.com/${bankData.ifsc_code}`);
        const data = await response.json();
        setBankData(prev => ({
          ...prev,
          bank_name: data.BANK || prev.bank_name,
          branch_name: data.BRANCH || prev.branch_name,
        }));
      } catch (error) {
        // Ignore
      }
    }
  };

  const handleSubmit = async () => {
    const requiredDocs = DOCUMENT_INFO.filter(d => d.required);
    const missingRequired = requiredDocs.filter(d => !uploads[d.type]);

    if (missingRequired.length > 0) {
      toast.error(`Missing required documents: ${missingRequired.map(d => d.label).join(', ')}`);
      setActiveTab('documents');
      return;
    }

    if (!validateBank()) {
      toast.error('Please complete bank details correctly');
      setActiveTab('bank');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      
      Object.entries(uploads).forEach(([type, data]) => {
        if (data.file) {
          formData.append(type, data.file);
          if (data.docNumber) formData.append(`${type}_number`, data.docNumber);
        }
      });

      const { confirm_account_number, ...submitBankData } = bankData;
      Object.entries(submitBankData).forEach(([key, value]) => {
        formData.append(key, value);
      });

      await sellerService.completeProfile(formData);
      toast.success('Profile completed successfully!');
      navigate('/seller/onboarding/status');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  const tabStyles = (isActive) => ({
    flex: 1,
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.medium,
    color: isActive ? theme.colors.primary.main : theme.colors.text.secondary,
    background: 'none',
    border: 'none',
    borderBottom: `2px solid ${isActive ? theme.colors.primary.main : 'transparent'}`,
    cursor: 'pointer',
    transition: theme.transitions.base,
  });

  return (
    <MainLayout>
      <div style={{ padding: theme.spacing.xl, maxWidth: '900px', margin: '0 auto' }}>
        <Card padding="xl">
          <h1 style={{ fontSize: theme.typography.fontSize['2xl'], fontWeight: theme.typography.fontWeight.bold, marginBottom: theme.spacing.xl }}>
            Complete Your Profile
          </h1>

          <div style={{ display: 'flex', borderBottom: `1px solid ${theme.colors.gray[200]}`, marginBottom: theme.spacing.xl }}>
            <button onClick={() => setActiveTab('documents')} style={tabStyles(activeTab === 'documents')}>Documents</button>
            <button onClick={() => setActiveTab('bank')} style={tabStyles(activeTab === 'bank')}>Bank Details</button>
          </div>

          {/* Always-mounted panels — prevents state loss when switching tabs */}
          <div style={{ display: activeTab === 'documents' ? 'block' : 'none' }}>
            {DOCUMENT_INFO.map(doc => (
              <DocumentUploadCard
                key={doc.type}
                docInfo={doc}
                onUpload={(data) => handleUpload(doc.type, data)}
                uploaded={!!uploads[doc.type]}
              />
            ))}
          </div>

          <div style={{ display: activeTab === 'bank' ? 'block' : 'none' }}>

              <FormInput label="Bank Name" name="bank_name" value={bankData.bank_name} onChange={handleBankChange} onBlur={handleBankBlur} error={errors.bank_name} touched={touched.bank_name} required />
              <FormInput label="Account Number" name="bank_account_number" value={bankData.bank_account_number} onChange={handleBankChange} onBlur={handleBankBlur} error={errors.bank_account_number} touched={touched.bank_account_number} required />
              <FormInput label="Confirm Account Number" name="confirm_account_number" value={bankData.confirm_account_number} onChange={handleBankChange} onBlur={handleBankBlur} onPaste={(e) => e.preventDefault()} error={errors.confirm_account_number} touched={touched.confirm_account_number} required />
              <FormInput label="IFSC Code" name="ifsc_code" value={bankData.ifsc_code} onChange={handleBankChange} onBlur={(e) => { handleBankBlur(e); handleIFSCBlur(); }} error={errors.ifsc_code} touched={touched.ifsc_code} placeholder="SBIN0001234" required />
              <FormInput label="Branch Name" name="branch_name" value={bankData.branch_name} onChange={handleBankChange} />
          </div>


          <div style={{ display: 'flex', gap: theme.spacing.md, marginTop: theme.spacing.xl }}>
            <Button variant="secondary" onClick={() => navigate('/seller/onboarding/status')} style={{ flex: 1 }}>Back</Button>
            <Button onClick={handleSubmit} loading={loading} style={{ flex: 1 }}>Complete Profile</Button>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default CompleteProfile;